import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, FlaskConical, Sparkles } from "lucide-react";
import api from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Newsletter from "@/components/Newsletter";
import { MedicalBadge } from "@/components/MedicalBadge";

export default function Home() {
  const { data: articles = [] } = useQuery({
    queryKey: ["articles"],
    queryFn: () => api.get("/articles").then((r) => r.data),
  });
  const { data: herbs = [] } = useQuery({
    queryKey: ["herbs"],
    queryFn: () => api.get("/herbs").then((r) => r.data),
  });
  const { data: therapies = [] } = useQuery({
    queryKey: ["therapies"],
    queryFn: () => api.get("/therapies").then((r) => r.data),
  });

  const hero = articles[0];

  return (
    <div>
      {/* Hero */}
      <section className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-12 sm:pt-16">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 flex flex-col justify-center"
          >
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-brand-line bg-white px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand">
              <Sparkles className="h-3.5 w-3.5 text-brand-clay" /> Independent Ayurveda Authority
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-brand-ink tracking-tight">
              Ancient wisdom,
              <br />
              <span className="text-brand">reviewed for today.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-brand-muted leading-relaxed">
              Trusted, reference-backed guidance on Ayurvedic therapies, herbs and traditional wellness —
              medically reviewed and safely shared across the SocialPulse network.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                data-testid="hero-explore-articles"
                to="/articles"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3.5 font-medium text-brand-sand transition-transform hover:scale-[1.02]"
              >
                Explore Articles <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                data-testid="hero-explore-therapies"
                to="/therapies"
                className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-6 py-3.5 font-medium text-brand-ink transition-colors hover:border-brand"
              >
                Browse Therapies
              </Link>
            </div>
          </motion.div>

          {hero && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <Link to={`/articles/${hero.slug}`} className="group block">
                <div className="relative overflow-hidden rounded-2xl border border-brand-line shadow-sm">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={hero.feature_image}
                      alt={hero.feature_alt}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-deep/90 to-transparent p-6 pt-16">
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-brand-sand/80">
                      Featured
                    </span>
                    <h3 className="mt-1.5 font-heading text-2xl text-white leading-snug">{hero.title}</h3>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* Featured articles */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 mt-24">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl text-brand-ink">Latest reviewed articles</h2>
            <p className="mt-2 text-brand-muted">Every article is checked by our editorial and medical reviewers.</p>
          </div>
          <Link to="/articles" data-testid="see-all-articles" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:gap-2.5 transition-all">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
          {articles.slice(1, 4).map((a, i) => (
            <ArticleCard key={a.slug} article={a} index={i} />
          ))}
        </div>
      </section>

      {/* Therapies strip */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 mt-24">
        <div className="rounded-3xl bg-brand-mist p-6 sm:p-10">
          <div className="flex items-center gap-2 text-brand-clay">
            <FlaskConical className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-widest">Therapies</span>
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-brand-ink">Traditional therapies, explained</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {therapies.map((t, i) => (
              <motion.div
                key={t.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <Link to={`/therapies/${t.slug}`} data-testid={`therapy-card-${t.slug}`} className="group block overflow-hidden rounded-xl border border-brand-line bg-white shadow-sm transition-transform hover:-translate-y-1">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={t.image} alt={t.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg text-brand-ink">{t.name}</h3>
                    <p className="mt-1 text-xs text-brand-muted">{t.duration}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Herbs strip */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 mt-24">
        <div className="flex items-center gap-2 text-brand-sage">
          <Leaf className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-widest">Herb Library</span>
        </div>
        <h2 className="mt-3 font-heading text-3xl sm:text-4xl text-brand-ink">Know your herbs</h2>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {herbs.slice(0, 6).map((h, i) => (
            <motion.div
              key={h.slug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
            >
              <Link to={`/herbs/${h.slug}`} data-testid={`herb-card-${h.slug}`} className="group block text-center">
                <div className="aspect-square overflow-hidden rounded-full border border-brand-line">
                  <img src={h.image} alt={h.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <p className="mt-3 text-sm font-medium text-brand-ink group-hover:text-brand">{h.name}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust band */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 mt-24">
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-brand-line bg-white p-8 text-center">
          <MedicalBadge reviewer="Dr. Meera Nair" />
          <p className="max-w-2xl text-brand-muted">
            Content on Ayurvedam.com is educational. High-risk health topics are held for human medical
            review and never auto-published. Always consult a qualified practitioner for personal advice.
          </p>
        </div>
      </section>

      <div className="mt-24">
        <Newsletter />
      </div>
    </div>
  );
}
